import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Stack, Typography, Button, Chip, Alert, Paper, Divider,
  CircularProgress, Tooltip, IconButton, Table, TableHead, TableRow,
  TableCell, TableBody,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { setCell, setBlendCell, switchMode } from '../../../../redux/tatWizardSlice';
import { stNameFromGroups, groupLabelFromApiGroup, ZONES, filterPricedCodes, findStGroup } from '../constants';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';
import TatCellInput from '../components/TatCellInput';
import TatExtractPreviewModal from '../components/TatExtractPreviewModal';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getLinkedCodesForSt(st, groups, pricedKeys) {
  const group = findStGroup(groups, st);
  const active = (group?.linkedCodes || []).filter((lc) => lc.isActive !== false);
  return filterPricedCodes(active, pricedKeys);
}

function getParentsForSt(st, groups, pricedKeys) {
  const codes = getLinkedCodesForSt(st, groups, pricedKeys);
  return [...new Set(codes.map((lc) => lc.parent || lc.cpAccountCode))];
}

function buildTemplate(st, mode, parents) {
  const wb = XLSX.utils.book_new();
  const makeSheet = () => {
    const wsData = [
      ['Zone', 'Min TAT (days)', 'Max TAT (days)'],
      ...ZONES.map((z) => [z.name, '', '']),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 20 }, { wch: 18 }, { wch: 18 }];
    return ws;
  };
  if (mode === 'blended') {
    XLSX.utils.book_append_sheet(wb, makeSheet(), 'TAT_Blended');
  } else {
    (parents.length ? parents : ['Courier']).forEach((p) => {
      XLSX.utils.book_append_sheet(wb, makeSheet(), p.slice(0, 31));
    });
  }
  return wb;
}

// Parse a single data row and return corrected [min, max].
// If both values are present and min > max, swap them automatically.
function parseRow(row) {
  const hasMin = row[1] !== '' && row[1] != null && !isNaN(Number(row[1]));
  const hasMax = row[2] !== '' && row[2] != null && !isNaN(Number(row[2]));
  let mn = hasMin ? Number(row[1]) : null;
  let mx = hasMax ? Number(row[2]) : null;
  if (mn != null && mx != null && mn > mx) { [mn, mx] = [mx, mn]; }
  return { mn, mx };
}

function parseAndDispatch(wb, st, mode, dispatch, parents) {
  const errors = [];
  if (mode === 'blended') {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1 });
    const data = rows.slice(1).filter((r) => r.length >= 1);
    if (!data.length) { errors.push('No data rows found.'); return errors; }
    data.slice(0, 5).forEach((row, idx) => {
      const z = idx + 1;
      const { mn, mx } = parseRow(row);
      if (mn != null) dispatch(setBlendCell({ st, z, bound: 'min', value: mn }));
      if (mx != null) dispatch(setBlendCell({ st, z, bound: 'max', value: mx }));
    });
  } else {
    if (!wb.SheetNames.length) { errors.push('No sheets found.'); return errors; }
    wb.SheetNames.forEach((sheetName) => {
      const parent = parents.find(
        (p) => p.toLowerCase() === sheetName.toLowerCase() ||
               sheetName.toLowerCase().includes(p.toLowerCase().slice(0, 8)),
      ) || sheetName;
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1 });
      const data = rows.slice(1).filter((r) => r.length >= 1);
      if (!data.length) { errors.push(`Sheet "${sheetName}": no data rows.`); return; }
      data.slice(0, 5).forEach((row, idx) => {
        const z = idx + 1;
        const { mn, mx } = parseRow(row);
        if (mn != null) dispatch(setCell({ st, parent, z, bound: 'min', value: mn }));
        if (mx != null) dispatch(setCell({ st, parent, z, bound: 'max', value: mx }));
      });
    });
  }
  return errors;
}

// ── Blended editor — zone cards layout (matches screenshot) ───────────────────

function BlendedEditor({ st, groups }) {
  const dispatch = useDispatch();
  const tatBlended = useSelector((s) => s.tatWizard.tatBlended[st] || {});
  const standardTat = useSelector((s) => s.tatWizard.standardTat?.[st]);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const linkedCodes = getLinkedCodesForSt(st, groups, pricedKeys);
  const parents = getParentsForSt(st, groups, pricedKeys);
  const accountCodeCount = linkedCodes.length;

  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
        <strong>Blended mode:</strong> one TAT row applies to all {accountCodeCount} account code(s) across {parents.length} parent(s) under this service type.
      </Typography>
      <Stack direction="row" spacing={1.25} flexWrap="wrap" rowGap={1.25}>
        {ZONES.map((z) => {
          const std = standardTat?.[String(z.id)];
          const stdLabel = std ? `Std: ${std[0]} → ${std[1]}d` : null;
          const mn = tatBlended[`z${z.id}_min`];
          const mx = tatBlended[`z${z.id}_max`];
          const zoneError = mn != null && mx != null && Number(mn) > Number(mx);
          return (
            <Box
              key={z.id}
              sx={{
                flex: '1 1 150px',
                minWidth: 130,
                border: 1,
                borderColor: zoneError ? '#ef4444' : 'divider',
                borderRadius: 1.5,
                p: 1.25,
                bgcolor: zoneError ? '#fff5f5' : 'background.paper',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2, mb: 0.25 }}>
                {z.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, display: 'block', mb: 1 }}>
                {z.desc}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: zoneError ? '#ef4444' : 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.25 }}>MIN</Typography>
                  <TatCellInput
                    value={mn}
                    std={std?.[0]}
                    source={mn != null ? 'agreement' : 'manual'}
                    hasError={zoneError}
                    onChange={(v) => dispatch(setBlendCell({ st, z: z.id, bound: 'min', value: v }))}
                    title={`Zone ${z.id} Min TAT`}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: zoneError ? '#ef4444' : 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.25 }}>MAX</Typography>
                  <TatCellInput
                    value={mx}
                    std={std?.[1]}
                    source={mx != null ? 'agreement' : 'manual'}
                    hasError={zoneError}
                    onChange={(v) => dispatch(setBlendCell({ st, z: z.id, bound: 'max', value: v }))}
                    title={`Zone ${z.id} Max TAT`}
                  />
                </Box>
              </Stack>
              {zoneError && (
                <Typography variant="caption" sx={{ color: '#c62828', fontSize: 10, display: 'block', mt: 0.75, fontWeight: 600 }}>
                  Min cannot exceed Max
                </Typography>
              )}
              {!zoneError && stdLabel && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10.5, display: 'block', mt: 0.75 }}>
                  {stdLabel}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

// ── Courier-wise editor — table: rows=couriers, cols=zones (matches screenshot) ─

function CourierWiseEditor({ st, groups }) {
  const dispatch = useDispatch();
  const tat = useSelector((s) => s.tatWizard.tat[st] || {});
  const standardTat = useSelector((s) => s.tatWizard.standardTat?.[st]);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const linkedCodes = getLinkedCodesForSt(st, groups, pricedKeys);
  const parents = getParentsForSt(st, groups, pricedKeys);

  if (!parents.length) {
    return <Alert severity="warning" sx={{ fontSize: 12 }}>No couriers mapped for this service type.</Alert>;
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 700, borderCollapse: 'separate', borderSpacing: 0 }}>
        <TableHead>
          {/* Zone header row */}
          <TableRow>
            <TableCell
              rowSpan={2}
              sx={{
                fontWeight: 700, fontSize: 12, verticalAlign: 'middle',
                bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
                borderBottom: '2px solid', borderColor: 'divider',
                minWidth: 160, position: 'sticky', left: 0, zIndex: 1,
              }}
            >
              Parent Courier
            </TableCell>
            {ZONES.map((z) => (
              <TableCell
                key={z.id}
                colSpan={2}
                align="center"
                sx={{
                  fontWeight: 700, fontSize: 12,
                  bgcolor: (t) => alpha(t.palette.grey[500], 0.06),
                  borderBottom: 1, borderLeft: '1px solid', borderColor: 'divider',
                  whiteSpace: 'nowrap',
                }}
              >
                {z.name}
              </TableCell>
            ))}
          </TableRow>
          {/* Min / Max sub-header */}
          <TableRow>
            {ZONES.map((z) => [
              <TableCell
                key={`${z.id}-min`}
                align="center"
                sx={{
                  fontWeight: 600, fontSize: 10, color: 'text.secondary',
                  textTransform: 'uppercase', letterSpacing: 0.4,
                  bgcolor: (t) => alpha(t.palette.grey[500], 0.04),
                  borderBottom: '2px solid', borderLeft: '1px solid', borderColor: 'divider',
                  py: 0.5, minWidth: 64,
                }}
              >
                Min
              </TableCell>,
              <TableCell
                key={`${z.id}-max`}
                align="center"
                sx={{
                  fontWeight: 600, fontSize: 10, color: 'text.secondary',
                  textTransform: 'uppercase', letterSpacing: 0.4,
                  bgcolor: (t) => alpha(t.palette.grey[500], 0.04),
                  borderBottom: '2px solid', borderColor: 'divider',
                  py: 0.5, minWidth: 64,
                }}
              >
                Max
              </TableCell>,
            ])}
          </TableRow>
        </TableHead>

        <TableBody>
          {parents.map((parent) => {
            const codes = linkedCodes.filter((lc) => (lc.parent || lc.cpAccountCode) === parent);
            const accountCodes = codes.map((lc) => lc.cpAccountCode).filter(Boolean);
            return (
              <TableRow key={parent} sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.02) }, '&:last-child td': { borderBottom: 0 } }}>
                {/* Parent courier cell */}
                <TableCell
                  sx={{
                    borderRight: '1px solid', borderColor: 'divider',
                    py: 1, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{parent}</Typography>
                  {accountCodes.slice(0, 2).map((ac) => (
                    <Typography key={ac} variant="caption" sx={{ color: 'text.disabled', fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, display: 'block' }}>
                      {ac}
                    </Typography>
                  ))}
                  {accountCodes.length > 2 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 9.5 }}>
                      +{accountCodes.length - 2} more
                    </Typography>
                  )}
                </TableCell>

                {/* Zone min/max inputs */}
                {ZONES.map((z) => {
                  const std = standardTat?.[String(z.id)];
                  const mn = tat[parent]?.[`z${z.id}_min`];
                  const mx = tat[parent]?.[`z${z.id}_max`];
                  const zoneError = mn != null && mx != null && Number(mn) > Number(mx);
                  return [
                    <TableCell key={`${z.id}-min`} sx={{ py: 0.5, px: 0.75, borderLeft: '1px solid', borderColor: 'divider', bgcolor: zoneError ? '#fff5f5' : 'inherit' }}>
                      <TatCellInput
                        value={mn}
                        std={std?.[0]}
                        source={mn != null ? 'agreement' : 'manual'}
                        hasError={zoneError}
                        onChange={(v) => dispatch(setCell({ st, parent, z: z.id, bound: 'min', value: v }))}
                        title={`${parent} Zone ${z.id} Min`}
                      />
                    </TableCell>,
                    <TableCell key={`${z.id}-max`} sx={{ py: 0.5, px: 0.75, bgcolor: zoneError ? '#fff5f5' : 'inherit' }}>
                      <TatCellInput
                        value={mx}
                        std={std?.[1]}
                        source={mx != null ? 'agreement' : 'manual'}
                        hasError={zoneError}
                        onChange={(v) => dispatch(setCell({ st, parent, z: z.id, bound: 'max', value: v }))}
                        title={`${parent} Zone ${z.id} Max`}
                      />
                    </TableCell>,
                  ];
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

// ── Single service-type card ───────────────────────────────────────────────────

function StCard({ st, groups }) {
  const dispatch = useDispatch();
  const mode = useSelector((s) => s.tatWizard.modeByST[st] || 'blended');
  const standardTat = useSelector((s) => s.tatWizard.standardTat?.[st]);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const [downloaded, setDownloaded] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileRef = useRef(null);

  const stName = stNameFromGroups(st, groups);
  const apiGroup = findStGroup(groups, st);
  const groupLabel = groupLabelFromApiGroup(apiGroup);
  const parents = getParentsForSt(st, groups, pricedKeys);
  const linkedCodes = getLinkedCodesForSt(st, groups, pricedKeys);

  const applyStandardTat = () => {
    if (!standardTat) return;
    ZONES.forEach((z) => {
      const std = standardTat[String(z.id)];
      if (!std) return;
      const [mn, mx] = std;
      if (mode === 'blended') {
        if (mn != null) dispatch(setBlendCell({ st, z: z.id, bound: 'min', value: mn }));
        if (mx != null) dispatch(setBlendCell({ st, z: z.id, bound: 'max', value: mx }));
      } else {
        parents.forEach((parent) => {
          if (mn != null) dispatch(setCell({ st, parent, z: z.id, bound: 'min', value: mn }));
          if (mx != null) dispatch(setCell({ st, parent, z: z.id, bound: 'max', value: mx }));
        });
      }
    });
  };

  const handleDownload = () => {
    XLSX.writeFile(buildTemplate(st, mode, parents), `TAT_Template_${st}_${mode}.xlsx`);
    setDownloaded(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadStatus('uploading');
    setErrorMsg('');
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const errors = parseAndDispatch(wb, st, mode, dispatch, parents);
      if (errors.length) {
        setUploadStatus('error');
        setErrorMsg(errors.join(' '));
      } else {
        setUploadStatus('success');
      }
    } catch {
      setUploadStatus('error');
      setErrorMsg('Could not read the file. Ensure it is a valid .xlsx file.');
    }
    e.target.value = '';
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* ── Header ── */}
      <Stack
        direction="row" alignItems="center" spacing={1} flexWrap="wrap" rowGap={0.5}
        sx={{ px: 2, py: 1, bgcolor: (t) => alpha(t.palette.grey[500], 0.04), borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {groupLabel} — {stName}
        </Typography>
        <Chip size="small" label={st} sx={{ height: 18, fontSize: 9.5, fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', bgcolor: (t) => alpha(t.palette.primary.main, 0.08), color: 'primary.dark' }} />

        {/* Mode toggle */}
        <Chip
          size="small"
          color={mode === 'blended' ? 'warning' : 'default'}
          label={mode === 'blended' ? 'Blended' : 'Courier wise'}
          sx={{ fontWeight: 700, fontSize: 11 }}
        />
        <Tooltip title={`Switch to ${mode === 'blended' ? 'Courier-wise' : 'Blended'}`}>
          <Button
            size="small" variant="text" startIcon={<SyncAltIcon sx={{ fontSize: 14 }} />}
            onClick={() => { dispatch(switchMode(st)); setDownloaded(false); setUploadStatus(null); }}
            sx={{ minWidth: 0, p: 0.5, fontSize: 11, textTransform: 'none', color: 'primary.main' }}
          >
            switch
          </Button>
        </Tooltip>

        <Box sx={{ flex: 1 }} />

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {parents.length} parents · {linkedCodes.length} account codes
        </Typography>

        {/* Upload controls in header */}
        {standardTat && (
          <Tooltip title="Fill all zones with the industry-standard TAT values">
            <Button size="small" variant="outlined" color="secondary"
              onClick={applyStandardTat}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: 11, height: 28 }}>
              Use standard TAT
            </Button>
          </Tooltip>
        )}
        <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
          onClick={handleDownload}
          sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: 11, height: 28 }}>
          Template
        </Button>
        <Tooltip title={!downloaded ? 'Download the template first' : ''}>
          <span>
            <Button size="small" variant="contained" component="label"
              startIcon={uploadStatus === 'uploading' ? <CircularProgress size={12} color="inherit" /> : <UploadFileIcon sx={{ fontSize: 13 }} />}
              disabled={!downloaded || uploadStatus === 'uploading'}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: 11, height: 28 }}>
              Upload
              <input hidden type="file" accept=".xlsx,.xls" ref={fileRef} onChange={handleFile} />
            </Button>
          </span>
        </Tooltip>
        {uploadStatus === 'success' && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
      </Stack>

      {/* Upload error */}
      {uploadStatus === 'error' && (
        <Alert severity="error" sx={{ mx: 2, mt: 1.25, py: 0.5, fontSize: 12 }}
          action={<IconButton size="small" onClick={() => setUploadStatus(null)}><CloseIcon fontSize="small" /></IconButton>}>
          {errorMsg}
        </Alert>
      )}

      {/* ── TAT Editor — always visible ── */}
      <Box sx={{ p: 2 }}>
        {mode === 'blended'
          ? <BlendedEditor st={st} groups={groups} />
          : <CourierWiseEditor st={st} groups={groups} />
        }
      </Box>
    </Paper>
  );
}

// ── Main stage ────────────────────────────────────────────────────────────────

export default function Stage2_Extract() {
  const selectedSTs = useSelector((s) => s.tatWizard.selectedSTs || []);
  const { data: groups = [] } = useGetServiceabilityGroupsQuery();
  const [previewOpen, setPreviewOpen] = useState(false);

  if (selectedSTs.length === 0) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        No service types selected. Go back to the Profile stage and select at least one.
      </Alert>
    );
  }

  return (
    <Box>
      <Alert severity="info" icon={false} sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>
        Enter TAT values directly or <strong>download the template → fill it in → upload</strong> to pre-populate the inputs. Values can be edited after upload.
      </Alert>

      <Stack spacing={2}>
        {selectedSTs.map((st) => (
          <StCard key={st} st={st} groups={groups} />
        ))}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<VisibilityOutlinedIcon />}
          onClick={() => setPreviewOpen(true)}
          sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
        >
          Preview &amp; Confirm
        </Button>
      </Stack>

      <TatExtractPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        hasTatTable
      />
    </Box>
  );
}
